import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { FileUpload } from "@/components/molecules/file-upload/index";
import { useUploadDocuments } from "@/features/documents/hooks/use-documents";
import { useTranslation } from "@/features/utils/i18n";
import { Upload } from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export const UploadDocumentDialog = () => {
  const { t } = useTranslation("documents");
  const { company: clientId } = useParams<{ company: string }>();
  const [open, onOpenChange] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);

  const uploadDocuments = useUploadDocuments();

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiles?.length || !clientId) return;

    try {
      await uploadDocuments.mutateAsync(selectedFiles);
      toast.success(
        t("messages.uploadSuccess", { count: selectedFiles.length }),
      );
      onOpenChange(false);
      setSelectedFiles(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.uploadError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Upload className="h-4 w-4" />
          {t("uploadDocuments")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleUpload} className="min-w-0">
          <DialogHeader>
            <DialogTitle>{t("upload.title")}</DialogTitle>
            <DialogDescription>{t("upload.description")}</DialogDescription>
          </DialogHeader>

          <div className="py-4 min-w-0">
            <FileUpload
              type="document"
              accept=".pdf,.docx,.html,.txt,.md"
              multiple
              value={selectedFiles}
              onChange={(files) => setSelectedFiles(files as File[] | null)}
              maxSize={50 * 1024 * 1024}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                setSelectedFiles(null);
              }}
            >
              {t("upload.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={uploadDocuments.isPending || !selectedFiles?.length}
            >
              {uploadDocuments.isPending && (
                <Spinner className="h-4 w-4 animate-spin" />
              )}
              {t("upload.upload")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
